import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, TriangleAlert } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { AuthCard } from "../components/AuthCard";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";

const BadLink = () => (
  <div data-testid="reset-invalid" className="mt-8 space-y-6">
    <div className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm font-semibold text-amber-800">
      <TriangleAlert className="h-5 w-5 shrink-0" />
      <p>This reset link is invalid, expired or already used.</p>
    </div>
    <Link to="/forgot-password" data-testid="reset-request-new-link" className="block text-center text-sm font-bold text-blue-700 hover:text-blue-800">Request a new reset link</Link>
  </div>
);

const Done = ({ onContinue }) => (
  <div data-testid="reset-success" className="mt-8 space-y-6">
    <div className="flex gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-4 text-sm font-semibold text-emerald-800">
      <CheckCircle2 className="h-5 w-5 shrink-0" />
      <p data-testid="reset-success-message">Your password has been reset</p>
    </div>
    <p className="text-sm text-slate-500">You're logged in on this device. Any other devices were signed out.</p>
    <Button data-testid="reset-continue-button" onClick={onContinue} className="h-12 w-full rounded-lg text-base font-semibold">Go to my dashboard</Button>
  </div>
);

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { authenticate } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [state, setState] = useState(token ? "form" : "invalid");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) return setError("Password must be at least 6 characters");
    if (form.password !== form.confirm) return setError("Passwords don't match");
    setError("");
    setBusy(true);
    try {
      await authenticate("reset-password", { token, new_password: form.password });
      setState("done");
    } catch (err) {
      if (err.response?.status === 400) setState("invalid");
      else setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const field = (key, label, testid) => (
    <div className="space-y-1.5">
      <Label htmlFor={testid} className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{label}</Label>
      <Input id={testid} type="password" autoComplete="new-password" data-testid={testid} className="h-12 rounded-xl bg-white" value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
    </div>
  );

  return (
    <AuthCard eyebrow="Password help" title="Choose a new password" testid="reset-password-card">
      {state === "invalid" && <BadLink />}
      {state === "done" && <Done onContinue={() => navigate("/dashboard")} />}
      {state === "form" && (
        <form onSubmit={submit} noValidate className="mt-8 space-y-5" data-testid="reset-password-form">
          {field("password", "New password", "reset-password-input")}
          {field("confirm", "Confirm new password", "reset-confirm-input")}
          <p className="text-xs text-slate-500">At least 6 characters. Resetting signs you out on all other devices.</p>
          {error && <p data-testid="reset-error" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{error}</p>}
          <Button type="submit" disabled={busy} data-testid="reset-submit-button" className="h-12 w-full rounded-lg text-base font-semibold">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Reset password"}
          </Button>
        </form>
      )}
    </AuthCard>
  );
}
